import React from "react";
import { Chart as ChartJS, registerables } from "chart.js";
import { Chart } from "react-chartjs-2";

ChartJS.register(...registerables);

const getChartData = (data, taskColors) => {
    if (!Array.isArray(data) || data.length === 0) return { labels: [], datasets: [] };

    const isSimpleFormat = data.every((item) => "label" in item && "data" in item);

    if (isSimpleFormat) {
        const validData = data.filter((item) => item.data !== null);
        return {
            labels: validData.map((item) => item.label),
            datasets: [
                {
                    label: "Tasks",
                    data: validData.map((item) => item.data),
                    backgroundColor: validData.map((item) => item.color || "#ccc"),
                    borderRadius: 4,
                    borderWidth: 0,
                },
            ],
        };
    }

    const taskTypes = [
        ...new Set(
            data.flatMap((project) =>
                Object.keys(project).filter((key) => key !== "label" && key !== "labelId")
            )
        ),
    ];

    return {
        labels: data.map((item) => item?.label || "Unknown Project"),
        datasets: taskTypes.map((type) => {
            const formattedType = type.toLowerCase().trim();
            return {
                label: formattedType,
                data: data.map((item) => item[type] || 0),
                backgroundColor: taskColors[formattedType] || "#ccc",
                borderRadius: 4,
                borderWidth: 0,
            };
        }),
    };
};

const getChartOptions = ({
    legendPosition,
    showLegend,
    scaleRatio,
    chartType,
    onBarClick,
    indexAxis,
    cutout,
    stacked = true,
    tooltipMode,
    tooltipIntersect,
}) => {
    const isBar = chartType === "bar";
    const isPie = chartType === "pie";
    const isDoughnut = chartType === "doughnut";
    const isRound = isPie || isDoughnut;
    const isHorizontal = isBar && indexAxis === "y";
    const resolvedTooltipMode = tooltipMode || "nearest";
    const resolvedTooltipIntersect =
        tooltipIntersect !== undefined ? tooltipIntersect : true;

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: isBar ? indexAxis || "x" : undefined,
        cutout: isDoughnut ? cutout || "68%" : undefined,
        onClick: onBarClick
            ? (event, elements, chart) => {
                  if (!elements?.length) return;
                  const el = elements[0];
                  const dataset = chart.data.datasets[el.datasetIndex];
                  const label = chart.data.labels[el.index];
                  onBarClick({
                      projectLabel: label,
                      statusLabel: dataset?.label,
                      datasetIndex: el.datasetIndex,
                      index: el.index,
                  });
              }
            : undefined,
        plugins: {
            legend: showLegend
                ? {
                      position: legendPosition || "bottom",
                      labels: {
                          usePointStyle: true,
                          pointStyle: "circle",
                          boxWidth: 10,
                          boxHeight: 10,
                          font: { size: 12 },
                          generateLabels: (chart) => {
                              const base =
                                  ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
                              return base.map((item) => ({
                                  ...item,
                                  text: item.text
                                      ? item.text.charAt(0).toUpperCase() + item.text.slice(1)
                                      : item.text,
                              }));
                          },
                      },
                  }
                : false,
            tooltip: {
                mode: resolvedTooltipMode,
                intersect: resolvedTooltipIntersect,
                backgroundColor: "rgba(15, 23, 42, 0.92)",
                titleFont: { size: 12, weight: "600" },
                bodyFont: { size: 12 },
                padding: 10,
                cornerRadius: 8,
                displayColors: true,
                filter: (tooltipItem) => {
                    const value =
                        tooltipItem.parsed?.x ??
                        tooltipItem.parsed?.y ??
                        tooltipItem.parsed ??
                        tooltipItem.raw ??
                        0;
                    return Number(value) > 0;
                },
                callbacks: {
                    title: (items) => {
                        if (!items?.length) return "";
                        return items[0].label || "";
                    },
                    label: (tooltipItem) => {
                        if (isRound) {
                            const value = tooltipItem.parsed ?? tooltipItem.raw ?? 0;
                            const data = tooltipItem.dataset?.data || [];
                            const total = data.reduce((sum, n) => sum + (Number(n) || 0), 0);
                            const pct = total > 0 ? Math.round((Number(value) / total) * 100) : 0;
                            return `${value} tasks (${pct}%)`;
                        }
                        const status = tooltipItem.dataset?.label
                            ? tooltipItem.dataset.label.charAt(0).toUpperCase() +
                              tooltipItem.dataset.label.slice(1)
                            : "Tasks";
                        const value =
                            tooltipItem.parsed?.x ??
                            tooltipItem.parsed?.y ??
                            tooltipItem.raw ??
                            0;
                        return `${status}: ${value}`;
                    },
                },
            },
        },
        layout: { padding: isHorizontal ? 8 : isRound ? 8 : 16 },
    };

    if (isBar) {
        // Category axis must NOT use beginAtZero — that forces numeric tick indices (0,1,2…)
        const valueAxis = {
            stacked,
            beginAtZero: true,
            grid: { display: true, drawBorder: false, color: "rgba(148, 163, 184, 0.25)" },
            ticks: {
                precision: 0,
                ...(scaleRatio ? { stepSize: scaleRatio } : {}),
            },
        };
        const categoryAxis = {
            stacked,
            type: "category",
            grid: { display: false, drawBorder: false },
            ticks: {
                autoSkip: false,
                font: { size: 11 },
                callback(value) {
                    const label = this.getLabelForValue(value);
                    if (!label) return "";
                    return label.length > 22 ? `${label.slice(0, 20)}…` : label;
                },
            },
        };

        options.scales = isHorizontal
            ? { x: valueAxis, y: categoryAxis }
            : { x: categoryAxis, y: valueAxis };

        options.datasets = {
            bar: {
                categoryPercentage: 0.65,
                barPercentage: 0.8,
                maxBarThickness: isHorizontal ? 28 : 48,
                borderRadius: 4,
            },
        };
    }

    return options;
};

export const RenderChart = ({
    data,
    chartType,
    height,
    width,
    legendPosition,
    showLegend,
    scaleRatio,
    taskColors,
    onBarClick,
    indexAxis,
    cutout,
    stacked = true,
    tooltipMode,
    tooltipIntersect,
}) => {
    const chartData = getChartData(data, taskColors);
    const chartOptions = getChartOptions({
        legendPosition,
        showLegend,
        scaleRatio,
        chartType,
        onBarClick,
        indexAxis,
        cutout,
        stacked,
        tooltipMode,
        tooltipIntersect,
    });
    return (
        <div className="chart-container" style={{ height, width }}>
            <Chart type={chartType} data={chartData} options={chartOptions} />
        </div>
    );
};
